import type { WheelAction } from '../../../stores/editorPrefsStore';

export const WHEEL_ZOOM_MIN = 0.1;
export const WHEEL_ZOOM_MAX = 5.0;

export interface WheelNavInput {
  deltaX: number;
  deltaY: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  clientX: number;
  clientY: number;
  canvasRectLeft: number;
  canvasRectTop: number;
  currentZoom: number;
  panX: number;
  panY: number;
  defaultAction: WheelAction;
  invertScroll: boolean;
  zoomSpeed: number;
}

export type WheelNavResult =
  | { kind: 'noop' }
  | { kind: 'pan'; panX: number; panY: number }
  | { kind: 'zoom'; zoom: number; panX: number; panY: number };

/**
 * Pure wheel-event router. Maps a WheelEvent + current viewport + user prefs
 * to either a pan delta or a cursor-anchored zoom.
 *
 * Routing rules:
 *   - ctrlKey or metaKey held (includes browser-synthesized trackpad pinch) -> zoom
 *   - defaultAction === 'zoom' and no modifier           -> zoom
 *   - defaultAction === 'pan' and no modifier            -> pan
 *   - shiftKey during pan                                 -> horizontal-only pan
 */
export function computeWheelNav(input: WheelNavInput): WheelNavResult {
  const {
    deltaX,
    deltaY,
    ctrlKey,
    metaKey,
    shiftKey,
    clientX,
    clientY,
    canvasRectLeft,
    canvasRectTop,
    currentZoom,
    panX,
    panY,
    defaultAction,
    invertScroll,
    zoomSpeed,
  } = input;

  const modifier = ctrlKey || metaKey;
  const shouldZoom = modifier || (!modifier && defaultAction === 'zoom');

  if (shouldZoom) {
    const speed = Math.min(3, Math.max(0.25, zoomSpeed));
    const absDelta = Math.abs(deltaY);
    const trackpadPinch = ctrlKey && absDelta < 40;
    const k = trackpadPinch ? 0.015 : 0.01;
    const factor = Math.exp(-deltaY * k * speed);
    let newZoom = currentZoom * factor;
    newZoom = Math.min(WHEEL_ZOOM_MAX, Math.max(WHEEL_ZOOM_MIN, newZoom));
    if (Math.abs(newZoom - currentZoom) < 1e-6) return { kind: 'noop' };

    const mouseX = clientX - canvasRectLeft;
    const mouseY = clientY - canvasRectTop;
    const artboardX = (mouseX - panX) / Math.max(currentZoom, 1e-6);
    const artboardY = (mouseY - panY) / Math.max(currentZoom, 1e-6);
    const newPanX = mouseX - artboardX * newZoom;
    const newPanY = mouseY - artboardY * newZoom;
    return { kind: 'zoom', zoom: newZoom, panX: newPanX, panY: newPanY };
  }

  const sign = invertScroll ? -1 : 1;
  let dx = deltaX * sign;
  let dy = deltaY * sign;
  if (shiftKey && Math.abs(dx) < Math.abs(dy)) {
    dx = dy;
    dy = 0;
  }
  if (dx === 0 && dy === 0) return { kind: 'noop' };
  return { kind: 'pan', panX: panX - dx, panY: panY - dy };
}
